/**
 * Inland Island Yeast Laboratories (Denver, Colorado) — the full liquid INIS
 * range, ninety-five strains.
 *
 * Every figure was read off Inland Island's own strain pages under
 * inlandislandyeast.com/yeast-library/ in September 2026, and every entry links
 * to the page its numbers came from. The lab publishes temperature in
 * Fahrenheit, so each band is converted and rounded to the nearest whole
 * degree: a 50–55 °F lager range shows here as 10–13 °C.
 *
 * Alcohol tolerance is `null` throughout, and deliberately. Inland Island rate
 * every strain with a word — "Medium", "High", "Very High" — and never a
 * number, and inventing the number is the one thing this package will not do.
 *
 * STA1 is `true` on the fifteen strains whose pages carry the lab's PCR
 * footnote confirming the glucoamylase gene, and `null` everywhere else: the
 * lab publishes no negative results, and unknown is not the same as safe.
 *
 * Flocculation is mapped onto the five-step vocabulary — "Very High" becomes
 * `high` (INIS-315), "Low - Medium" becomes `medium-low`, "Medium - High"
 * becomes `medium-high`. Three of the four lactobacillus pages print no
 * flocculation row at all, an IBU tolerance standing in its place; they carry
 * `low` here, which is what a bacterial pitch does, and they are marked
 * `representative` for it.
 *
 * Two conversions needed a decision. Where the lab prints an open-ended
 * ceiling — the kveik at "70-100+ °F", INIS-296 at "72-80+" — the published
 * number is taken as the top and the "+" dropped, which understates rather
 * than invents. And where a converted ceiling passes the schema's 45 °C bound
 * (INIS-991 and INIS-992 are rated to 115 °F) it is clamped to 45, and the
 * description says so.
 *
 * Marked `representative`, with the reason repeated in each description:
 *
 * - `inland-island-442`, `-901`, `-902`, `-903`, `-910`, `-913`, `-960` — the
 *   lab publishes attenuation open-ended ("80+", "85+", "90+"), so the upper
 *   bound is ours.
 * - `inland-island-920`, `-930` — the only temperature published is an
 *   open-ended "85+ °F", so the band is ours.
 * - `inland-island-991`, `-992`, `-993`, `-995` — pure lactobacillus pitches.
 *   Inland Island publish attenuation as "<5", below the schema's floor of 5,
 *   so the band is nominal, as Escarpment's Lactobacillus Blend 2.0 already
 *   is; three of the four also publish no flocculation.
 *
 * Two things are deliberately absent. Inland Island's dry library (INIS-1001D
 * Dry Chico and its siblings) is repackaged dry yeast sold under a separate
 * catalogue, and this file is the liquid range the plan asked for. And the
 * "INIS-9999 Another Strain" page is a custom-propagation service, not a
 * product, so nothing was entered for it.
 */

import type { Yeast } from '../../schema/yeast';

/** Inland Island Yeast Laboratories cultures. @see Yeast */
export const inlandIslandYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // American ales
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-001',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-001',
    name: 'Northern California Ale Yeast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Chico strain, and the reason it became the default American ale yeast: a clean, low-ester fermentation that repitches for generation after generation without drifting. Inland Island trace their culture to a craft brewery in Chico, California. Pale ales, IPAs and anything where the malt and hop bill should do the talking.',
    tags: ['chico', 'american-ale', 'clean', 'neutral', 'pale-ale', 'ipa', 'house-strain'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-001-northern-california-ale-yeast/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-003',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-003',
    name: 'Vermont IPA Yeast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'An East Coast isolate that leaves noticeably more body and fruit than a Chico-type strain, and holds its ester profile across a wide temperature band rather than flipping character with a couple of degrees. Inland Island point it at double IPAs and bigger hazy beers, where the extra mouthfeel is the point.',
    tags: ['vermont', 'hazy', 'neipa', 'ipa', 'fruity', 'full-body', 'biotransformation'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-003-colorado-ipa-yeast/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-004',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-004',
    name: 'Oregon IPA Yeast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A rounded Northwest ale strain that keeps a malt backbone under the hops, throws a modest fruity ester and then drops hard. As happy in a porter or a stout as it is in a Northwest pale ale.',
    tags: ['pacific-northwest', 'ipa', 'malty', 'flocculent', 'porter', 'stout', 'pale-ale'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-004-oregon-ipa-yeast/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-005',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-005',
    name: 'Southern California Ale Yeast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 85 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Inland Island describe it as INIS-001 with the edges filed off: a cleaner ester profile, more headroom on gravity and a shorter fermentation. The pick for a West Coast IPA or a double IPA that has to turn around quickly.',
    tags: ['west-coast', 'ipa', 'clean', 'fast', 'high-gravity', 'pale-ale'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-005-southern-california-ale-yeast/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-006',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-006',
    name: 'Eccentric Ale Yeast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Stops short of dry and leaves complex esters behind, which is exactly what a malt-forward IPA or a big English-leaning porter, stout or barleywine wants. Not the strain to reach for when you want the hops alone.',
    tags: ['malt-forward', 'estery', 'ipa', 'porter', 'stout', 'barleywine', 'full-body'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-006-eccentric-ale-yeast/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-007',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-007',
    name: 'American Ale Blend',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 20, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A blend of fruity American IPA strains from the Northwest, Northeast and Midwest, pitched together for an ester profile no single culture in the range gives. It finishes less dry than INIS-001 or INIS-005 but slots into the same beers.',
    tags: ['blend', 'american-ale', 'estery', 'ipa', 'pale-ale', 'complex'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-007-american-ale-blend/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-008',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-008',
    name: 'East Coast IPA',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A London brewery isolate that found a second career when New England IPA arrived. It ferments malty and hop-forward at once and leaves a little sugar behind, which is where the soft, full palate of a hazy comes from.',
    tags: ['hazy', 'neipa', 'english-ale', 'ipa', 'malty', 'residual-sweetness'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-008-east-coast-ipa/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-009',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-009',
    name: 'Fog Machine',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 19, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "Inland Island's purpose-built hazy strain, and the one that took a GABF gold in hazy pale ale in 2020. Pitched under juicy hop varieties it pushes esters up and lifts hop aroma with them: passion fruit skin and blood orange, with a herbal, faintly dank edge underneath.",
    tags: [
      'hazy',
      'neipa',
      'ipa',
      'passion-fruit',
      'blood-orange',
      'biotransformation',
      'award-winning',
    ],
    url: 'https://inlandislandyeast.com/yeast-library/inis-009-fog-machine/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-010',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-010',
    name: 'American Ale Yeast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A common brewery house strain: flocculent enough to drop bright on its own, quiet on esters, and clean over the malt. A little more complex than INIS-001 without giving up any of its reliability.',
    tags: ['american-ale', 'house-strain', 'clean', 'flocculent', 'malty', 'pale-ale'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-010-american-ale-yeast/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-012',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-012',
    name: 'Pacific Ale Yeast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Pacific Northwest strain that flocculates hard and leaves a clean, faintly fruity beer over a malty base. Useful where a brewer wants brightness without fining.',
    tags: ['pacific-northwest', 'flocculent', 'clean', 'fruity', 'malty', 'pale-ale'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-012-pacific-ale-yeast/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-055',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-055',
    name: 'American Weizen',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The American wheat strain: it leaves the beer permanently cloudy but produces very little ester, so a wheat beer fermented with it tastes of grain rather than banana. Made for American wheat ales and Northwest-style hefeweizens.',
    tags: ['american-wheat', 'hazy', 'low-ester', 'wheat', 'cream-ale'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-055-american-weizen/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Belgian ales
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-201',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-201',
    name: 'Belgian Monk Ale I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'From a monastery brewery in southern Belgium. High alcohol tolerance and a dark plum note make it the obvious pick for dubbel and Belgian dark strong, where the fruit has to survive a big gravity.',
    tags: ['belgian', 'trappist', 'dubbel', 'dark-strong', 'plum', 'high-gravity', 'phenolic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-201-belgian-monk-ale-i/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-202',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-202',
    name: 'Belgian Monk Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Isolated from a monastery brewery known for very strong ales with a lot going on. It gives both esters and phenols in quantity, which is what a tripel or a Belgian golden strong is built around.',
    tags: ['belgian', 'trappist', 'tripel', 'golden-strong', 'estery', 'phenolic', 'high-gravity'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-202-belgian-monk-ale-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-203',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-203',
    name: 'Belgian Monk Ale III',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 18, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The restrained Trappist option: full bodied and malty with a clean fermentation, and only a hint of the dried fruit INIS-201 gives freely. Good where the grain bill is meant to carry the beer.',
    tags: ['belgian', 'trappist', 'clean', 'full-body', 'dried-fruit', 'dubbel'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-203-belgian-monk-ale-iii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-204',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-204',
    name: 'Belgian Monk Ale IV',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A monastery isolate that finishes dry with a faint acidity and is otherwise unusually clean for a Belgian strain. Inland Island recommend it as a primary under a Brettanomyces secondary, and the dry finish is why.',
    tags: ['belgian', 'trappist', 'dry', 'acidic-finish', 'brett-friendly', 'clean'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-204-belgian-monk-ale-iv/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-222',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-222',
    name: 'Belgian Gnome Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The estery-and-phenolic Belgian all-rounder, and the most flocculent of the Belgian strains here, so the beer clears without help. It covers most of the Belgian style range on its own.',
    tags: ['belgian', 'estery', 'phenolic', 'flocculent', 'versatile', 'saison', 'tripel'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-222-belgian-gnome-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-225',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-225',
    name: 'Belgian American Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 79 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A Belgian-style strain from a well-known brewery near Montreal. It ferments dry with restrained esters, and the phenols climb as the fermentation temperature does. Inland Island have confirmed the STA1 gene by PCR, so it will keep working on dextrins.',
    tags: ['belgian', 'dry', 'low-ester', 'phenolic', 'sta1', 'diastatic', 'tripel'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-225-belgian-american-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-227',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-227',
    name: 'Flemish Fiend Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 72 },
    temperatureC: { min: 22, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A West Flanders isolate with the usual Belgian orange-peel and bready character plus clove, ginger, hay and a mineral spine that reads more saison than Belgian ale. It attenuates low and leaves real sweetness on the palate.',
    tags: [
      'belgian',
      'west-flanders',
      'clove',
      'ginger',
      'mineral',
      'residual-sweetness',
      'low-attenuation',
    ],
    url: 'https://inlandislandyeast.com/yeast-library/inis-227-flemish-fiend-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-231',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-231',
    name: 'Belgian Gold Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'The Belgian strong golden strain: a very high alcohol ceiling with mild phenols and fruity esters rather than the full phenolic assault. Inland Island have confirmed STA1 by PCR.',
    tags: ['belgian', 'golden-strong', 'high-gravity', 'phenolic', 'estery', 'sta1', 'diastatic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-231-belgian-gold-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-241',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-241',
    name: 'Belgian Wit Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A witbier strain that crops so heavily it needs headspace planning. It gives pear, apple and stone fruit with a slight tartness in the finish.',
    tags: ['witbier', 'belgian', 'pear', 'apple', 'stone-fruit', 'tart', 'top-cropping'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-241-belgian-wit-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-242',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-242',
    name: 'Belgian Wit Devil Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Close to INIS-241 but with more clove and phenol. It is extremely top heavy — Inland Island warn that without generous headspace a fair amount of the pitch leaves through the blow-off.',
    tags: ['witbier', 'belgian', 'phenolic', 'clove', 'top-cropping', 'spicy'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-242-belgian-wit-devil-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-243',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-243',
    name: 'Belgian Wit II Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 19, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The peppery wit: white and black pepper over the usual spice. It can be slow to start, so nutrient and a good oxygen charge at pitch are worth the trouble.',
    tags: ['witbier', 'belgian', 'pepper', 'spicy', 'slow-start', 'phenolic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-243-belgian-wit-ii-ale/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Saison
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-291',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-291',
    name: 'Saison: Farmhouse',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 21, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A wide-ranging farmhouse strain: strawberry and hay over spice, a slightly tart finish and a mouthfeel better than the attenuation suggests. PCR-confirmed STA1, which is how it gets there.',
    tags: [
      'saison',
      'farmhouse',
      'phenolic',
      'estery',
      'strawberry',
      'hay',
      'sta1',
      'diastatic',
      'tart',
    ],
    url: 'https://inlandislandyeast.com/yeast-library/inis-291-saison-farmhouse/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-292',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-292',
    name: 'Saison: Belgian I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 21, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'The classic Belgian saison strain, fruity and spicy and bone dry — and the one famous for parking at around 5–7 °P before it finishes. Give it heat and time rather than a fresh pitch. PCR-confirmed STA1.',
    tags: ['saison', 'belgian', 'dry', 'tart', 'spicy', 'stalling', 'sta1', 'diastatic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-292-saison-belgian-i/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-293',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-293',
    name: 'Saison: Belgian II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'More fruit and far fewer stalls than INIS-292, at the cost of some depth. Inland Island note the two are rumoured to be halves of one mixed culture, and they do complement each other. PCR-confirmed STA1.',
    tags: ['saison', 'belgian', 'fruity', 'reliable', 'sta1', 'diastatic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-293-saison-belgian-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-295',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-295',
    name: 'Saison: Phantom',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A saison strain with subtle spice and moderate esters that finishes dry and tart without the sluggishness the Belgian saison strains are known for. PCR-confirmed STA1.',
    tags: ['saison', 'dry', 'tart', 'spicy', 'reliable', 'sta1', 'diastatic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-295-saison-phantom/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-296',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-296',
    name: 'Saison: Walloon',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 88 },
    temperatureC: { min: 22, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'Mild acidity, subtle spicy phenols and a light funk, with the balance between phenol and ester swinging hard on fermentation temperature. PCR-confirmed STA1.',
    tags: ['saison', 'funk', 'acidity', 'phenolic', 'temperature-driven', 'sta1', 'diastatic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-296-saison-walloon/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-298',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-298',
    name: 'Saison: Baron',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 21, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A French bière de garde yeast rather than a Belgian saison one: briny, with white pepper and straw. The strain to reach for when a saison should not taste like every other saison.',
    tags: ['saison', 'biere-de-garde', 'french', 'white-pepper', 'briny', 'straw'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-298-saison-baron/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-491',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-491',
    name: 'Saison: French',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 18, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'The French saison strain: more phenol than its Belgian cousins, far less likely to stall, and peppery and citrusy with spice behind. It attenuates hard and still leaves a decent mouthfeel. PCR-confirmed STA1.',
    tags: ['saison', 'french', 'pepper', 'citrus', 'attenuative', 'mouthfeel', 'sta1', 'diastatic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-491-saison-french/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // English, Scottish and Irish ales
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-301',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-301',
    name: 'Dublin Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 71, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Dublin brewery isolate that ferments dry with very little fruit when kept cool, and turns estery above about 19 °C. Stouts, porters and Irish reds.',
    tags: ['irish', 'dry', 'stout', 'porter', 'irish-red', 'low-ester', 'flocculent'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-301-dublin-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-302',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-302',
    name: 'Scotch Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 73 },
    temperatureC: { min: 13, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Scottish strain: full bodied and malty with a clean finish. Inland Island rate it as a do-anything house yeast, and its wide temperature band backs that up.',
    tags: ['scottish', 'malty', 'full-body', 'house-strain', 'wee-heavy', 'versatile'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-302-scotch-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-303',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-303',
    name: 'London Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Used where an English beer needs to attenuate further than an English strain usually will. Crisp and fruity with an oaky edge to the esters.',
    tags: ['english', 'attenuative', 'crisp', 'fruity', 'oak', 'porter', 'stout'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-303-london-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-312',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-312',
    name: 'English Ale II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 75 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Clean and neutral to the point of self-effacement, which is the point: the grain and the hops arrive unmediated. A safe English choice for a bitter or a golden ale.',
    tags: ['english', 'clean', 'neutral', 'bitter', 'porter', 'showcase'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-312-english-ale-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-313',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-313',
    name: 'English Ale III',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 71 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Leaves a full malty body and flocculates hard enough that the beer comes bright without fining or filtration. A bitter strain first and foremost.',
    tags: ['english', 'malty', 'full-body', 'flocculent', 'bright', 'bitter'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-313-english-ale-iii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-314',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-314',
    name: 'English Ale IV',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Medium bodied, a little malty, a little fruity, and flocculent enough to leave the beer bright. The all-rounder of the English set.',
    tags: ['english', 'medium-body', 'malty', 'fruity', 'flocculent', 'bitter'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-314-english-ale-iv/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-315',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-315',
    name: 'English Ale V',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 63, max: 70 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'One of the most widely used English house strains, and the driest of Inland Island’s English set. Extremely flocculent — the lab prints its flocculation as very high — so plan on rousing or a longer rest. Extra special bitter, mild, porter and stout.',
    tags: ['english', 'esb', 'flocculent', 'dry', 'mild', 'porter', 'stout', 'house-strain'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-315-english-ale-v/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-316',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-316',
    name: 'English Ale VI',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 76 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The textbook British ale yeast: crisp, clean and fairly dry, with enough malt left to read as English and enough flocculation to drop bright.',
    tags: ['english', 'crisp', 'clean', 'dry', 'malty', 'flocculent', 'bitter'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-316-english-ale-vi/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-317',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-317',
    name: 'High Gravity Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 95 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'The gravity specialist. At ordinary strengths it reads as a malty English strain; as the gravity climbs the esters build with it, and Inland Island rate it for the very biggest beers in the book. PCR-confirmed STA1.',
    tags: ['high-gravity', 'barleywine', 'imperial-stout', 'estery', 'malty', 'sta1', 'diastatic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-317-high-gravity-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-318',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-318',
    name: 'English Dry Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Brews much the same beer as INIS-315 but reaches terminal gravity faster and drier, which makes it the better choice under a high-gravity stout, IPA or barleywine.',
    tags: ['english', 'dry', 'fast', 'high-gravity', 'stout', 'ipa', 'barleywine'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-318-english-dry-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-320',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-320',
    name: 'Nottingham Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 76 },
    temperatureC: { min: 10, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Vigorous to the point of finishing fermentable sugar in about five days, and neutral enough that only a trace of fruit shows up. A workhorse rather than a character strain.',
    tags: ['nottingham', 'fast', 'neutral', 'clean', 'versatile', 'flocculent'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-320-nottingham-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-330',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-330',
    name: 'Aussie Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Australian sparkling ale strain: malty and clean with a bready ester note, and it stays clean at the warm end of its band where most English strains do not.',
    tags: ['australian', 'malty', 'bready', 'clean', 'warm', 'sparkling-ale'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-330-aussie-ale/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Kveik
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-441',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-441',
    name: 'Norwegian Farmhouse',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 76, max: 78 },
    temperatureC: { min: 21, max: 38 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Norwegian farmhouse culture with the usual kveik trick: ferment it hot and fast, or keep it cool and get almost no aroma at all. Flocculation is better than average even for a kveik.',
    tags: ['kveik', 'norwegian', 'fast', 'hot', 'flocculent', 'ipa', 'pale-ale'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-441-norwegian-farmhouse/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-442',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-442',
    name: 'White Witch',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 80, max: 88 },
    temperatureC: { min: 21, max: 38 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'The odd one out in the kveik set. It finishes bone dry because it carries STA1 — Inland Island confirmed the gene by PCR — and it leads with clove and allspice rather than the usual kveik citrus, with a faint banana that hints at a hefeweizen ancestor. Good in a Belgian blonde or as blending stock in a mixed culture.',
    tags: ['kveik', 'diastatic', 'sta1', 'clove', 'allspice', 'banana', 'dry', 'blending'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-442-white-witch/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-443',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-443',
    name: 'The Viking',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 21, max: 38 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Shares the orange citrus of INIS-441 but adds caramel, herb and pine, with a waxy funk underneath. It sits well on a richer grain bill and earthy hops.',
    tags: ['kveik', 'norwegian', 'orange', 'caramel', 'pine', 'funky', 'malty'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-443-the-viking/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-444',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-444',
    name: 'TRVE Norwegian',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 21, max: 38 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Voss-type marmalade character with cantaloupe, citronella and white pepper alongside, finishing mildly sweet with a hint of minerality. Built to free-rise and tear through a farmhouse ale or a citrusy IPA.',
    tags: ['kveik', 'voss', 'orange-marmalade', 'cantaloupe', 'white-pepper', 'fast', 'hot'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-444-trve-norwegian/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-445',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-445',
    name: 'The Strand',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 18, max: 38 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The most versatile kveik in the library, and the one with the widest temperature range: clover honey at one end, bruised mango and overripe pineapple at the other. It will do a modern hoppy ale, an English style or a funky farmhouse depending where you hold it.',
    tags: ['kveik', 'versatile', 'honey', 'mango', 'pineapple', 'wide-temperature', 'farmhouse'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-445-the-strand/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-446',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-446',
    name: 'Tropic Express',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 21, max: 35 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Tropical from top to bottom — pineapple and passion fruit through guava and dried mango. A hoppy pale ale or IPA strain, or a table beer that needs a fruit backbone.',
    tags: ['kveik', 'tropical', 'pineapple', 'passion-fruit', 'guava', 'mango', 'ipa'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-446-tropic-express/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-447',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-447',
    name: 'Nordic Way',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 21, max: 38 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The clean kveik: lager-like restraint at temperatures no lager strain would survive. Inland Island say it will ferment out in three days at 37 °C with little risk of off-flavors.',
    tags: ['kveik', 'clean', 'lager-like', 'fast', 'hot', 'neutral'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-447-nordic-way/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-448',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-448',
    name: 'Lithuanian Farmhouse',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 80, max: 85 },
    temperatureC: { min: 21, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A Lithuanian farmhouse culture that gives citrus esters without piling on phenols — in effect a fast, warm substitute for a wit or saison strain. PCR-confirmed STA1.',
    tags: ['kveik', 'lithuanian', 'farmhouse', 'citrus', 'saison', 'witbier', 'sta1', 'diastatic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-448-lithuanian-farmhouse/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Continental European ales
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-451',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-451',
    name: 'French Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 75 },
    temperatureC: { min: 17, max: 23 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A French ale strain for bière de garde and specialty beers: clean, low on esters compared with the Belgian strains, and content to let the malt bill lead.',
    tags: ['french', 'biere-de-garde', 'clean', 'low-ester', 'malty', 'saison'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-451-french-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-461',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-461',
    name: 'Continental Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 16, max: 18 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A malty Swedish strain that flocculates hard enough to clear a beer without filtration. It leaves a little more diacetyl than comparable strains, so give it a rest before packaging.',
    tags: ['swedish', 'malty', 'flocculent', 'bright', 'diacetyl', 'bitter'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-461-continental-ale/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // German wheat
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-541',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-541',
    name: 'German Wheat',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The classic wheat strain, and the one whose character you choose at pitch: stress it and you get banana, hold it cool with a proper pitch rate and good oxygen and you get clove.',
    tags: ['hefeweizen', 'weizen', 'clove', 'banana', 'phenolic', 'wheat'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-541-german-wheat/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-542',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-542',
    name: 'German Weizen',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Versatile and loud: pear, banana and apple esters together with clove and other phenols. It builds a tall krausen, so leave room in the fermenter.',
    tags: ['hefeweizen', 'weizen', 'banana', 'pear', 'apple', 'clove', 'top-cropping', 'wheat'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-542-german-weizen/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-544',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-544',
    name: 'Prost Weizen',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'The northern German hefeweizen profile — clove and vanilla phenols rather than banana esters. PCR-confirmed STA1, so it will keep attenuating past where a wheat strain usually stops.',
    tags: ['hefeweizen', 'weizen', 'clove', 'vanilla', 'phenolic', 'sta1', 'diastatic', 'wheat'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-544-prost-weizen/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-545',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-545',
    name: 'Classic Hefeweizen',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The balanced hefeweizen strain: banana and clove in roughly equal measure, with neither taking the beer over.',
    tags: ['hefeweizen', 'weizen', 'banana', 'clove', 'balanced', 'wheat'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-545-classic-hefeweizen/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-546',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-546',
    name: 'German Weizen II',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The banana option. It pushes isoamyl acetate hard and gives less clove than INIS-542, and it flocculates and attenuates a little further than that strain too. Expect a tall krausen.',
    tags: ['hefeweizen', 'weizen', 'banana', 'isoamyl-acetate', 'flocculent', 'wheat'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-546-german-weizen-ii/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Kolsch, alt and other German hybrids
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-571',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-571',
    name: 'Kolsch Style Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 13, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A cool-fermenting ale strain for kölsch and alt, leaving a slight fruitiness behind. It flocculates poorly, so the beer needs a long cold rest to come bright.',
    tags: ['kolsch', 'altbier', 'clean', 'cool-fermentation', 'low-flocculation', 'fruity'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-571-kolsch-style-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-572',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-572',
    name: 'Golden Kolsch Style Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 13, max: 18 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A German house strain that gets out of the way of the hops and finishes crisp and lager-like. Light fruit and some sulfur early on, both of which fade with a little conditioning.',
    tags: ['kolsch', 'clean', 'crisp', 'lager-like', 'sulfur', 'house-strain'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-572-golden-kolsch-style-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-573',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-573',
    name: 'German Hybrid Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Aimed at kölsch and altbier: a slight sulfur note during fermentation that conditions out, leaving a clean, lager-like ale.',
    tags: ['kolsch', 'altbier', 'clean', 'lager-like', 'sulfur'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-573-german-hybrid-ale/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-574',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-574',
    name: 'Kolsch II',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A kölsch strain that will work several degrees warmer than most and still drop clear. Expect a hint of fruit and some sulfur, both of which clean up with age.',
    tags: ['kolsch', 'warm', 'flocculent', 'lager-like', 'sulfur', 'crisp'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-574-kolsch-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-576',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-576',
    name: 'German Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 65, max: 72 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A traditional alt and kölsch strain that ferments clean and finishes a shade sweet, with very little ester when held at the cool end.',
    tags: ['altbier', 'kolsch', 'clean', 'low-ester', 'malty', 'residual-sweetness'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-576-german-ale/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Sake
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-607',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-607',
    name: 'Sake No. 7',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 100 },
    temperatureC: { min: 21, max: 38 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The most widely used sake yeast, kept for its consistency rather than its aroma. Its ester production is more restrained than INIS-609, and it will take a rice ferment to a very high alcohol.',
    tags: ['sake', 'rice', 'high-gravity', 'neutral', 'attenuative'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-607-sake-7/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-609',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-609',
    name: 'Sake No. 9',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 17, max: 20 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'The ginjo-shu yeast: a strong fermenter with far more aromatic lift than INIS-607, and one of the most important strains in modern sake making. PCR-confirmed STA1.',
    tags: ['sake', 'ginjo', 'aromatic', 'rice', 'sta1', 'diastatic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-609-sake-9/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Lager
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-701',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-701',
    name: 'Session Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The American light lager strain: dry, clean and built for a highly carbonated, thirst-quenching beer that nobody has to think about.',
    tags: ['american-lager', 'light-lager', 'dry', 'clean', 'sessionable'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-701-session-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-702',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-702',
    name: 'Classic Pilz',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 77 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A European pilsner strain that leaves a malty finish under an otherwise clean fermentation.',
    tags: ['pilsner', 'european-lager', 'malty', 'clean'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-702-classic-pilz/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-711',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-711',
    name: 'German Monk Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 74 },
    temperatureC: { min: 10, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "Inland Island's best-selling lager yeast, and the easiest of them to carry through several generations. It ferments clean over a solid malt body and flocculates better than most lager strains, so the beer comes bright.",
    tags: ['bavarian', 'helles', 'malty', 'flocculent', 'repitch', 'house-strain'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-711-german-monk-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-715',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-715',
    name: 'Belgian Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A European lager all-rounder with unusually little sulfur, which makes it a short-conditioning choice for American and European pilsners alike.',
    tags: ['low-sulfur', 'pilsner', 'versatile', 'clean'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-715-belgian-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-718',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-718',
    name: 'Danish Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The old Northern European lager strain made famous in Copenhagen. Clean and undemanding, and suited to the kind of lager that gets drunk rather than analyzed.',
    tags: ['danish', 'carlsberg', 'clean', 'crisp', 'pale-lager'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-718-danish-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-721',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-721',
    name: 'Old German Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 74 },
    temperatureC: { min: 11, max: 17 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Munich Helles strain: clean, with an early sulfur note that conditions away given a proper lagering.',
    tags: ['helles', 'munich', 'malty', 'sulfur', 'clean'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-721-old-german-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-730',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-730',
    name: 'Steamer Lager',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 65, max: 70 },
    temperatureC: { min: 14, max: 18 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The California common strain, from a West Coast lager brewery: a lager yeast that ferments at ale temperatures and stays clean doing it. Fruitiness climbs with the thermometer.',
    tags: ['california-common', 'steam', 'warm', 'clean', 'fruity', 'flocculent'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-730-steamer-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-740',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-740',
    name: 'Fest Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 65, max: 73 },
    temperatureC: { min: 11, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The Märzen and Oktoberfest strain. It needs a real lagering period to finish clean and fully attenuated — rushed, it shows.',
    tags: ['marzen', 'oktoberfest', 'malty', 'lagering', 'festbier'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-740-fest-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-745',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-745',
    name: 'Bohemian Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 78, max: 82 },
    temperatureC: { min: 12, max: 15 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Bavarian lager strain used across most of the lager range for its clean finish and malty backbone. One of the most common lager cultures in the industry.',
    tags: ['bavarian', 'malty', 'clean', 'versatile', 'pilsner', 'helles'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-745-bohemian-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-752',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-752',
    name: 'Bavarian Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 68, max: 76 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A pilsner and Helles strain with a balanced aroma over a malty base. Slight sulfur, and low diacetyl for a lager yeast.',
    tags: ['pilsner', 'helles', 'malty', 'balanced', 'sulfur', 'low-diacetyl'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-752-bavarian-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-755',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-755',
    name: 'Bock Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A hearty malt character without much residual sweetness, and enough balance to carry a full-bodied bock or doppelbock — or a good deal else.',
    tags: ['bock', 'doppelbock', 'malty', 'full-body', 'balanced'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-755-bock-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-760',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-760',
    name: 'Czech Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A low-diacetyl Czech strain that finishes crisp and dry. The lager yeast to pick when the beer has nowhere to hide.',
    tags: ['czech', 'pilsner', 'crisp', 'dry', 'low-diacetyl'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-760-czech-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-761',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-761',
    name: 'Czech II',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 9, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A traditional Czech lager strain with more ester than most and very little diacetyl.',
    tags: ['czech', 'estery', 'low-diacetyl', 'pilsner'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-761-czech-ii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-762',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-762',
    name: 'Czech VI',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 8, max: 12 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A strong-fermenting Czech strain with low ester, fusel and diacetyl production — the clean end of the Czech set.',
    tags: ['czech', 'strong-fermenter', 'low-ester', 'pilsner'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-762-czech-vi/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-763',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-763',
    name: 'Czech CXLIII',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 9, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'German in origin despite the Czech numbering, and pointed at the malty end of the lager range: bock, doppelbock, Oktoberfest and Helles.',
    tags: ['bock', 'doppelbock', 'marzen', 'helles', 'german', 'malty'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-763-czech-cxliii/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-770',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-770',
    name: 'Mexican Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 78 },
    temperatureC: { min: 10, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Sold for Mexican-style light lager, but clean and crisp enough to stand in for almost any lager strain in the book.',
    tags: ['mexican-lager', 'clean', 'crisp', 'versatile', 'pale-lager'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-770-mexican-lager/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-780',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-780',
    name: 'Atlas Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 80 },
    temperatureC: { min: 17, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A pressure-fermentation strain: it will lager at close to room temperature and produce a clean, already-carbonated beer inside a fortnight. Sulfur is heavy for the first few days and then tails off.',
    tags: ['high-pressure', 'fast', 'warm', 'sulfur', 'pressure-fermentation', 'clean'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-780-atlas-lager/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Champagne and seltzer
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-801',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-801',
    name: 'Champagne Yeast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 85, max: 100 },
    temperatureC: { min: 16, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The champagne strain, for anything that has to finish bone dry or ferment a sugar that is not maltose: cider, mead, wine, seltzer, a Brut IPA. Neutral, POF-positive, and with a high alcohol ceiling.',
    tags: [
      'champagne',
      'cider',
      'mead',
      'wine',
      'seltzer',
      'brut-ipa',
      'attenuative',
      'high-gravity',
    ],
    url: 'https://inlandislandyeast.com/yeast-library/inis-801-champagne-yeast/',
    provenance: 'spec-sheet',
  },
  {
    id: 'inland-island-802',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-802',
    name: 'Seltzer Yeast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 98, max: 100 },
    temperatureC: { min: 20, max: 21 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Bred for a hundred per cent simple-sugar wash. Given nutrient and a fining agent it will finish a seltzer in under a week, leaving something bright with a faint white-wine note and nowhere for a flavor addition to hide. Inland Island publish attenuation as 100 % on dextrose; the band here is that figure with the usual tolerance.',
    tags: ['seltzer', 'dextrose', 'fast', 'neutral', 'white-wine', 'sugar-wash'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-802-seltzer-yeast/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Brettanomyces and wild Saccharomyces
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-901',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-901',
    name: 'Brettanomyces Bruxellensis I',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 90, max: 98 },
    temperatureC: { min: 16, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The barnyard Brettanomyces: horse, sweat and goat, and six months to finish if you let it. Inland Island recommend a Saccharomyces for the primary and this underneath it. The published attenuation is open-ended at 90 % and up, so the upper bound here is ours.',
    tags: ['brett', 'barnyard', 'horse-blanket', 'secondary', 'slow', 'attenuative'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-901-brettanomyces-bruxellensis-i/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-902',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-902',
    name: 'Brettanomyces Bruxellensis II',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 85, max: 93 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Brettanomyces that will take a beer on its own without a Saccharomyces primary, finishing complex, fruity and sour. Inland Island publish attenuation as 85 % and up, so the upper bound here is ours.',
    tags: ['brett', 'primary', 'fruity', 'sour', 'complex'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-902-brettanomyces-bruxellensis-ii/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-903',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-903',
    name: 'Brett Brux III',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 90, max: 98 },
    temperatureC: { min: 16, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Milder than INIS-901 with more tropical fruit, and capable of a primary fermentation unaided. Pitched alongside a lactobacillus it makes a straightforwardly sour beer. Attenuation is published as 90 % and up; the upper bound here is ours.',
    tags: ['brett', 'primary', 'tropical', 'mild', 'sour', 'lacto-friendly'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-903-brett-brux-iii/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-910',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-910',
    name: 'Wild Sacch',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 90, max: 98 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A wild Saccharomyces quick enough to finish a primary in under a week. Held hot it gives saison-like phenols, held cool it comes out clean, and it suits kettle sours and mixed fermentations either way. PCR-confirmed STA1. Attenuation is published as 90 % and up, so the upper bound here is ours.',
    tags: [
      'wild-sacch',
      'fast',
      'saison-like',
      'phenolic',
      'kettle-sour',
      'mixed-fermentation',
      'sta1',
      'diastatic',
    ],
    url: 'https://inlandislandyeast.com/yeast-library/inis-910-wild-sacch/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-913',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-913',
    name: 'Brett Barrel Yeast III',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 90, max: 98 },
    temperatureC: { min: 16, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'From an American wild ale brewery, and able to ferment without a Saccharomyces alongside. Mild acidity and tropical fruit, with a very light mouthfeel that the lab suggests building back with the mash or an adjunct. Attenuation is published as 90 % and up; the upper bound is ours.',
    tags: ['brett', 'primary', 'tropical', 'acidity', 'light-body', 'barrel-aged'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-913-brett-barrel-yeast-iii/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-920',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-920',
    name: 'Brettanomyces Lambicus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 85 },
    temperatureC: { min: 29, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Textbook Brett: horse blanket and spice with pineapple underneath, from spontaneously fermented beer. As a primary it takes well over a month. Inland Island publish only an open-ended 29 °C and up for temperature, so the band here is ours.',
    tags: ['brett', 'lambic', 'horse-blanket', 'spice', 'pineapple', 'slow', 'barrel-aged'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-920-brettanomyces-lambicus/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-930',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-930',
    name: 'Brettanomyces Claussenii',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 85 },
    temperatureC: { min: 29, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The gentlest Brettanomyces in the range — pineapple aroma and very little of the funk that puts people off Brett beers. Inland Island publish only an open-ended 29 °C and up for temperature, so the band here is ours.',
    tags: ['brett', 'claussenii', 'pineapple', 'mild', 'barrel-aged', 'brett-ipa'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-930-brettanomyces-claussenii/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-960',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-960',
    name: 'Fruit Sacch',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 88 },
    temperatureC: { min: 21, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A wild Saccharomyces that behaves like a Brettanomyces: run warm it gives pineapple and mango while attenuating past 80 %. Useful in a citrusy beer that wants a little funk without a long secondary. PCR-confirmed STA1, and the published attenuation is open-ended, so the upper bound here is ours.',
    tags: [
      'wild-sacch',
      'brett-like',
      'tropical',
      'pineapple',
      'mango',
      'attenuative',
      'sta1',
      'diastatic',
    ],
    url: 'https://inlandislandyeast.com/yeast-library/inis-960-fruit-sacch/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-970',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-970',
    name: 'Lachancea Thermotolerans',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Lachancea thermotolerans that is pitched and handled like an ordinary yeast but makes lactic acid as it ferments, souring the beer in one step. Inland Island warn that the acid makes apparent attenuation read lower than it really is.',
    tags: ['lachancea', 'sour', 'lactic', 'kettle-sour', 'berliner-weisse', 'single-pitch'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-970-lachancea-thermotolerans/',
    provenance: 'spec-sheet',
  },
  // -------------------------------------------------------------------------
  // Bacteria
  // -------------------------------------------------------------------------
  {
    id: 'inland-island-991',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-991',
    name: 'Lactobacillus brevis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 5, max: 5 },
    temperatureC: { min: 32, max: 45 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A lactobacillus for kettle souring: tart, with pineapple and green apple. It makes more acid the hotter and the less hopped the wort is. It produces lactic acid rather than alcohol, so a Saccharomyces still has to ferment the beer; the attenuation figure here is nominal against a published under 5 %, and the 46 °C top of its published range is clamped to the 45 °C ceiling this catalog allows.',
    tags: [
      'lactobacillus',
      'kettle-sour',
      'sour',
      'berliner-weisse',
      'pineapple',
      'green-apple',
      'hot',
    ],
    url: 'https://inlandislandyeast.com/yeast-library/inis-991-lactobacillus-brevis/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-992',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-992',
    name: 'Lactobacillus casei',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 5, max: 5 },
    temperatureC: { min: 24, max: 45 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A clean, tart lactobacillus with starfruit, guava and lychee. It will not tolerate more than about 5 IBU, which is precisely why it is safe to bring into a working brewery. Pitch a Saccharomyces to make the alcohol; the attenuation figure here is nominal against a published under 5 %, and the 46 °C top of its published range is clamped to the 45 °C ceiling this catalog allows.',
    tags: ['lactobacillus', 'kettle-sour', 'sour', 'starfruit', 'guava', 'lychee', 'hop-sensitive'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-992-lactobacillus-casei/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-993',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-993',
    name: 'Lactobacillus plantarum',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 5, max: 5 },
    temperatureC: { min: 29, max: 41 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Lactobacillus plantarum for kettle souring and co-fermentation, making a lot of lactic acid with tropical fruit and citrus behind it. Inland Island rate it, like their other lacto strains, for worts under about 5 IBU. It sours rather than ferments, so a yeast pitch still follows; the attenuation figure here is nominal against a published under 5 %.',
    tags: ['lactobacillus', 'plantarum', 'kettle-sour', 'sour', 'tropical', 'citrus', 'probiotic'],
    url: 'https://inlandislandyeast.com/yeast-library/inis-993-lactobacillus-plantarum/',
    provenance: 'representative',
  },
  {
    id: 'inland-island-995',
    lab: 'Inland Island Yeast',
    productCode: 'INIS-995',
    name: 'Lactobacillus buchneri',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 5, max: 5 },
    temperatureC: { min: 18, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A heterofermentative lactobacillus: lactic and acetic acid plus a little CO2, which makes for a sharper, more aggressive sour than the other lacto strains here, with plenty of tropical fruit alongside. It is the most hop-tolerant of the four, rated to about 10 IBU. Attenuation is nominal against a published under 5 %.',
    tags: [
      'lactobacillus',
      'buchneri',
      'heterofermentative',
      'lambic',
      'gueuze',
      'berliner-weisse',
      'acetic',
      'sour',
    ],
    url: 'https://inlandislandyeast.com/yeast-library/inis-995-lactobacillus-buchneri/',
    provenance: 'representative',
  },
];
